import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useRef } from 'react';

import type { Cart } from '@getjust/gateway';

import { CART_QUERY_KEY } from '$src/constants';
import { useBaseApiUrl } from '$src/hooks/client/useBaseApiUrl';
import { useCartIdUpdater } from '$src/hooks/client/useCartIdUpdater';
import { useCart } from '$src/hooks/queries';
import { gateway } from '$src/http';
import { SetPickupPointInput } from '$src/pages/api/[shopId]/[cartId]/set-pickup-point';

export const SET_PICKUP_POINT_MUTATION_KEY = 'MUTATION/SET_PICKUP_POINT';

type SetPickUpPointMutationContext = { previousCart: Cart | undefined };

type UseSetPickUpPointReturn = ReturnType<
  typeof useMutation<AxiosResponse<Cart>, Error, SetPickupPointInput, SetPickUpPointMutationContext>
> & { cancel: () => void };

export function useSetPickUpPoint(): UseSetPickUpPointReturn {
  const queryClient = useQueryClient();
  const baseUrl = useBaseApiUrl();
  const updateCartId = useCartIdUpdater();
  const { data: cart } = useCart();
  const abortControllerRef = useRef<AbortController | null>(null);

  const mutation = useMutation({
    mutationFn: (pickupPoint: SetPickupPointInput) => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      return gateway.post<Cart>(`${baseUrl}/set-pickup-point`, pickupPoint, { signal });
    },
    mutationKey: [SET_PICKUP_POINT_MUTATION_KEY, baseUrl],
    onMutate: async ({ pickupPoint, shippingMethodId }) => {
      await queryClient.cancelQueries({ queryKey: [CART_QUERY_KEY] });
      const previousCart = queryClient.getQueryData<Cart>([CART_QUERY_KEY, cart?.id]);
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, previousCart?.id], (oldCart): Cart | undefined => {
        if (!oldCart) return oldCart;
        const selected = oldCart?.shipping?.methods?.find((method) => method.id === shippingMethodId);
        if (!selected) return oldCart;
        return {
          ...oldCart,
          shipping: {
            ...oldCart.shipping,
            selected: [
              {
                ...selected,
                pickupPoint: {
                  id: pickupPoint.id,
                  zip: pickupPoint.zip,
                  city: pickupPoint.city,
                  name: pickupPoint.name,
                  type: pickupPoint.type,
                  address1: pickupPoint.address1,
                },
              },
            ],
          },
        };
      });

      return {
        previousCart,
      };
    },
    onError: (error, _shippinMethod, context) => {
      if (error instanceof AxiosError && error.code !== 'ERR_CANCELED') {
        // we do not reset query data if the error is due to an abort, to avoid glitch
        queryClient.setQueryData<Cart>([CART_QUERY_KEY, context?.previousCart?.id], context?.previousCart);
      }
    },
    onSuccess: ({ data }, variables) => {
      // Stale-response guard. `cancel()` aborts the AbortController, but a
      // response that's already in flight (or partially received) can land
      // AFTER the abort signal and resolve successfully here — even though
      // the caller has moved on. Without this guard, the backend snapshot
      // for the OLD shipping method clobbers the cache and the UI snaps
      // back to the old method ~300-500ms after the user picked a new one.
      // Trace: useUpdateShippingMethod → updateCart(B), this onSuccess →
      // setQueryData(cartSnapshotShowingA) → cart reverts to A.
      const currentCart = queryClient.getQueryData<Cart>([CART_QUERY_KEY, data.id]);
      const currentSelectedId = currentCart?.shipping?.selected?.[0]?.id;
      if (currentSelectedId && currentSelectedId !== variables.shippingMethodId) {
        return;
      }
      updateCartId(data.id);
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, data.id], data);
    },
  });

  const cancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current?.abort();
    }
  };

  return { ...mutation, cancel };
}
